import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Shield, Zap } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const PRINCIPLES = [
  {
    icon: Sparkles,
    title: 'Brilliance',
    desc: "Like rhodium's unmatched reflectivity, we amplify artists who shine with authentic creativity and refuse to blend into the background."
  },
  {
    icon: Shield,
    title: 'Strength',
    desc: 'We build lasting artistic legacies that withstand trends and time, creating music with structural integrity and cultural impact.'
  },
  {
    icon: Zap,
    title: 'Untarnished',
    desc: 'Our artists maintain their unique identities and artistic vision, preserving what makes them original while reaching new audiences.'
  }
];

const PageHeader = () => (
  <div className="mb-16 text-center">
    <div className="flex justify-center items-center gap-6 mb-8">
      <img
        src="/rhodium-logo.png"
        alt="Rhodium Records"
        className="logo-img h-24 w-24 md:h-32 md:w-32 object-contain"
        style={{ animation: 'pulse 3s ease-in-out infinite' }}
      />
    </div>
    <h1 className="text-5xl md:text-7xl font-black mb-4">
      <span className="text-white">Element </span>
      <span className="logo-text text-silver-400">45</span>
    </h1>
    <p className="text-silver-400 text-xl md:text-2xl font-light italic mb-8">
      Where Originality Is Amplified
    </p>
  </div>
);

const PhilosophySection = () => (
  <div className="max-w-5xl mx-auto mb-20">
    <div className="bg-gradient-to-br from-silver-900/30 to-silver-900/10 border-2 border-silver-400/30 rounded-2xl p-8 md:p-12 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-silver-400/5 rounded-full blur-3xl" />
      <div className="relative z-10">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-full bg-silver-400/20 flex items-center justify-center">
            <Sparkles size={32} className="text-silver-400" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            The <span className="text-silver-400">Rhodium</span> Standard
          </h2>
        </div>
        <p className="text-white/90 text-lg md:text-xl leading-relaxed mb-6">
          Rhodium is one of the rarest, most reflective elements on Earth, known for its brilliance,
          strength, and refusal to tarnish, and Rhodium Records is built the same way, crafting music
          that cuts through the noise and lasts.
        </p>
        <p className="text-white/90 text-lg md:text-xl leading-relaxed">
          Element 45 ("Rhodium") on the Periodic Table is a home for artists who move different, where
          originality is amplified, identities stay sharp, and every record is polished to hit with
          lasting impact.
        </p>
      </div>
    </div>
  </div>
);

const PrinciplesSection = () => (
  <div className="mb-20">
    <h3 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
      <span className="text-white">Our </span>
      <span className="text-silver-400">Principles</span>
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {PRINCIPLES.map(({ icon: Icon, title, desc }) => (
        <div
          key={title}
          className="bg-silver-900/10 border border-silver-900/20 hover:border-silver-400/30 rounded-xl p-8 transition-all duration-300 hover:transform hover:scale-105"
        >
          <div className="w-14 h-14 rounded-full bg-silver-400/10 flex items-center justify-center mb-6">
            <Icon size={28} className="text-silver-400" />
          </div>
          <h4 className="text-2xl font-bold text-white mb-4">{title}</h4>
          <p className="text-white/70 leading-relaxed">{desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const FlagshipArtistCTA = () => (
  <div className="max-w-4xl mx-auto">
    <div className="bg-gradient-to-r from-silver-900/20 to-transparent border border-silver-400/20 rounded-2xl p-8 md:p-12">
      <div className="flex flex-col md:flex-row items-center gap-8">
        <div className="flex-shrink-0">
          <img
            src="/olah-bliss.png"
            alt="Òlah Bliss"
            className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-silver-400/30"
          />
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">Meet Our Flagship Artist</h3>
          <p className="text-white/70 text-lg mb-6">
            Discover Òlah Bliss, Element 45's visionary experimental artist crafting ethereal
            soundscapes that transcend traditional boundaries.
          </p>
          <Link
            to="/element45/olah-bliss"
            className="inline-flex items-center gap-2 px-8 py-4 bg-silver-400 hover:bg-silver-300 text-black font-bold text-sm uppercase tracking-wider rounded-lg transition-all duration-300 hover:scale-105"
          >
            Explore Òlah Bliss
            <ArrowRight size={20} />
          </Link>
        </div>
      </div>
    </div>
  </div>
);

const PeriodicElementDisplay = () => (
  <div className="mt-20 text-center">
    <div className="inline-block bg-silver-900/20 border-2 border-silver-400/30 rounded-xl p-8">
      <div className="text-silver-400 text-sm uppercase tracking-widest mb-2">Atomic Number</div>
      <div className="text-7xl font-black text-white mb-2">45</div>
      <div className="text-3xl font-bold logo-text mb-4">Rh</div>
      <div className="text-silver-400 text-lg">Rhodium</div>
      <div className="text-white/60 text-sm mt-4">102.91 u</div>
    </div>
  </div>
);

const Element45Overview = () => {
  usePageMeta(
    'Element 45 | Rhodium Records',
    "Element 45 — Rhodium Records' experimental imprint where originality is amplified. Home of flagship artist Òlah Bliss."
  );
  return (
  <div className="min-h-screen bg-black pt-24 pb-32">
    <div className="container mx-auto px-6">
      <PageHeader />
      <PhilosophySection />
      <PrinciplesSection />
      <FlagshipArtistCTA />
      <PeriodicElementDisplay />
    </div>
  </div>
  );
};

export default Element45Overview;
